import React, { useState, useEffect, useMemo } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../config/axios';

const FirmsPage = () => {
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingFirm, setEditingFirm] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [refreshingWhatsApp, setRefreshingWhatsApp] = useState(false);
  const [hoveredAction, setHoveredAction] = useState(null);
  
  const MOBILE_BREAKPOINT = 1200;
  const SCREENSHOT_TOKEN = 'fbajans-admin-secret-2025';

  const getScreenshotUrl = (slug) => `https://panel.fbajans.com/api/screenshot/${encodeURIComponent(slug)}?token=${SCREENSHOT_TOKEN}`;

  const openScreenshotLink = (slug) => {
    if (!slug) return;
    window.open(getScreenshotUrl(slug), '_blank', 'noopener,noreferrer');
  };

  // Filtreleme state'leri
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    whatsappStatus: 'all',
    proxyStatus: 'all',
    licenseStatus: 'all'
  });
  
  // İstatistikler
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    whatsappConnected: 0,
    whatsappWaiting: 0,
    proxyWorking: 0,
    licenseExpired: 0
  });
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    phone: '',
    email: '',
    address: '',
    tax_number: '',
    tax_office: '',
    gpt_model: 'auto',
    license_start: '',
    license_expire: '',
    message_limit: 0,
    proxy_host: '',
    proxy_port: '',
    proxy_user: '',
    proxy_pass: '',
    status: 'active',
    api_enabled: true,
    bulk_message_enabled: false
  });

  // Yeni firma oluştururken entegrasyon seçimi
  const [integrationType, setIntegrationType] = useState('opencart'); // 'opencart' | 'vaktinibildir' | 'webhook'
  const [vbCustomerId, setVbCustomerId] = useState('');
  const [webhookProvider, setWebhookProvider] = useState('shopify'); // shopify | ikas | ticimax
  const [webhookSecret, setWebhookSecret] = useState('');

  // Check if mobile ve title ayarla
  useEffect(() => {
    // Tarayıcı title'ını ayarla
    document.title = 'FBAJANS WhatsApp Business - Firma Yönetimi';
    
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Firmaları admin server'dan getir ve WhatsApp durumlarını kontrol et
  useEffect(() => {
    const fetchFirms = async () => {
      try {
        const res = await api.get('/firms');
        console.log('API Response:', res.data); // Debug için
        console.log('Is Array:', Array.isArray(res.data.data)); // Debug için
        console.log('Setting firms:', res.data.data); // Debug için
        
        const firmsData = Array.isArray(res.data.data) ? res.data.data : [];
        
        // Her firma için WhatsApp durumunu kontrol et
        const firmsWithWhatsAppStatus = await Promise.all(
          firmsData.map(async (firm) => {
            if (firm.slug) {
              try {
                const whatsappRes = await fetch(`https://panel.fbajans.com/api/whatsapp-service/status/${firm.slug}`);
                const whatsappData = await whatsappRes.json();
                if (whatsappData.success) {
                  console.log(`✅ ${firm.slug} WhatsApp durumu: ${whatsappData.data.data.status}`);
                  return {
                    ...firm,
                    whatsapp_status: whatsappData.data.data.status,
                    live_status: whatsappData.data.data.status
                  };
                }
              } catch (whatsappErr) {
                console.log(`⚠️ ${firm.slug} WhatsApp kontrol hatası: ${whatsappErr.message}`);
                return {
                  ...firm,
                  whatsapp_status: 'error',
                  live_status: 'error'
                };
              }
            }
            return firm;
          })
        );
        
        setFirms(firmsWithWhatsAppStatus);
      } catch (err) {
         console.error('Fetch error:', err); // Debug için
         setError(err.message);
      } finally { setLoading(false); }
    };
    fetchFirms();
  }, []);

  // Tüm sistem verilerini güncelle (WhatsApp, Proxy, Lisans, vs.)
  const refreshAllSystemData = async () => {
    setRefreshingWhatsApp(true);
    try {
      // 1. Firma listesini yenile
      const firmsRes = await api.get('/firms');
      const firmsData = Array.isArray(firmsRes.data.data) ? firmsRes.data.data : [];
      
      // 2. Her firma için WhatsApp durumunu kontrol et ve state'i güncelle
      const updatedFirms = await Promise.all(
        firmsData.map(async (firm) => {
          if (firm.slug) {
            try {
              const whatsappRes = await fetch(`/api/whatsapp-service/status/${firm.slug}`);
              const whatsappData = await whatsappRes.json();
              if (whatsappData.success) {
                console.log(`✅ ${firm.slug} WhatsApp durumu: ${whatsappData.data.data.status}`);
                return {
                  ...firm,
                  whatsapp_status: whatsappData.data.data.status,
                  live_status: whatsappData.data.data.status
                };
              }
            } catch (whatsappErr) {
              console.log(`⚠️ ${firm.slug} WhatsApp kontrol hatası: ${whatsappErr.message}`);
              return {
                ...firm,
                whatsapp_status: 'error',
                live_status: 'error'
              };
            }
          }
          return firm;
        })
      );
      
      // State'i güncelle
      setFirms(updatedFirms);
      
      setSuccess('✅ Tüm sistem verileri güncellendi! WhatsApp, Proxy, Lisans ve diğer bilgiler kontrol edildi.');
      setTimeout(() => setSuccess(''), 5000);
      
    } catch (err) {
      setError('❌ Sistem verileri güncellenemedi: ' + err.message);
      setTimeout(() => setSuccess(''), 5000);
    } finally {
      setRefreshingWhatsApp(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleOpenModal = (firm = null) => {
    if (firm) {
      setEditingFirm(firm);
      console.log('Editing firm data:', firm); // Debug için
      setFormData({
        name: firm.name || '',
        slug: firm.slug || '',
        phone: firm.phone || '',
        email: firm.email || '',
        address: firm.address || '',
        tax_number: firm.tax_number || '',
        tax_office: firm.tax_office || '',
        gpt_model: firm.gpt_model || 'auto',
        license_start: firm.license_start ? firm.license_start.split('T')[0] : '',
        license_expire: firm.license_expire ? firm.license_expire.split('T')[0] : '',
        message_limit: firm.message_limit || 0,
        proxy_host: firm.proxy_host || '',
        proxy_port: firm.proxy_port || '',
        proxy_user: firm.proxy_user || '',
        proxy_pass: firm.proxy_pass || '',
        status: firm.status || 'active',
        api_enabled: typeof firm.api_enabled === 'boolean' ? firm.api_enabled : !!firm.api_enabled,
        bulk_message_enabled: typeof firm.bulk_message_enabled === 'boolean' ? firm.bulk_message_enabled : !!firm.bulk_message_enabled
      });
      // Edit modunda entegrasyon seçimi gösterilmiyor
      setIntegrationType('opencart');
      setVbCustomerId('');
      setWebhookProvider('shopify');
      setWebhookSecret('');
    } else {
      setEditingFirm(null);
      setFormData({
        name: '',
        slug: '',
        phone: '',
        email: '',
        address: '',
        tax_number: '',
        tax_office: '',
        gpt_model: 'auto', 
        license_start: '', 
        license_expire: '', 
        message_limit: 0, 
        proxy_host: '', 
        proxy_port: '', 
        proxy_user: '', 
        proxy_pass: '', 
        status: 'active',
        api_enabled: true,
        bulk_message_enabled: false
      });

      setIntegrationType('opencart');
      setVbCustomerId('');
      setWebhookProvider('shopify');
      setWebhookSecret('');
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingFirm(null);
    setFormData({
      name: '',
      slug: '',
      phone: '',
      email: '',
      address: '',
      tax_number: '',
      tax_office: '',
      gpt_model: 'auto', 
      license_start: '', 
      license_expire: '', 
      message_limit: 0, 
      proxy_host: '', 
      proxy_port: '', 
      proxy_user: '', 
      proxy_pass: '',
      status: 'active',
      api_enabled: true,
      bulk_message_enabled: false
    });

    setIntegrationType('opencart');
    setVbCustomerId('');
    setWebhookProvider('shopify');
    setWebhookSecret('');
  };

  const applyVaktiniBildirDefaults = (customerId) => {
    const cleanId = String(customerId || '').trim();
    const slug = cleanId ? `vb_${cleanId}` : '';

    setFormData(prev => ({
      ...prev,
      slug,
      api_enabled: false
    }));
  };

  // Admin server endpoint'lerini kullan
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      let res;
      if (editingFirm) {
        res = await api.put(`/firms/${editingFirm.id}`, formData);
      } else {
        // Create payload (formData mutasyonunu önlemek için kopya)
        const payload = { ...formData };

        if (integrationType === 'vaktinibildir') {
          const cleanId = String(vbCustomerId || '').trim();
          if (!cleanId) {
            setError('VaktiniBildir müşteri ID zorunludur');
            setLoading(false);
            return;
          }

          payload.slug = `vb_${cleanId}`;
          payload.api_enabled = false; // VaktiniBildir firmalarında OpenCart API menüsü/erişimi kapalı
        }

        if (integrationType === 'webhook') {
          // Webhook firmalarında OpenCart API menüsü/erişimi varsayılan kapalı olsun
          payload.api_enabled = false;
        }

        res = await api.post('/firms', payload);

        // Webhook seçildiyse: integrations kaydı oluştur
        if (integrationType === 'webhook') {
          const firmSlug = res?.data?.slug || payload.slug;
          try {
            const created = await api.post('/integrations', {
              firm_slug: firmSlug,
              provider: webhookProvider,
              status: 'active',
              settings: {},
              secrets: webhookSecret ? { webhook_secret: webhookSecret } : {}
            });

            const integrationId = created?.data?.data?.id;
            if (integrationId) {
              const url = `${window.location.origin}/api/integrations/${integrationId}/webhook/${webhookProvider}`;
              window.alert(`✅ Webhook entegrasyonu hazır!\n\nFirma: ${firmSlug}\nProvider: ${webhookProvider}\nWebhook URL: ${url}`);
            } else {
              window.alert(`⚠️ Firma oluşturuldu ama entegrasyon oluşturulamadı. Slug: ${firmSlug}`);
            }
          } catch (intErr) {
            console.error('Webhook integration create error:', intErr);
            window.alert(`⚠️ Firma oluşturuldu ama entegrasyon oluşturulamadı. Slug: ${firmSlug}`);
          }
        }

        // VaktiniBildir seçildiyse otomatik lisans oluştur + site bağla
        if (integrationType === 'vaktinibildir') {
          const firmSlug = res?.data?.slug || payload.slug;

          try {
            const gen = await api.post('/licenses/generate', { firm_slug: firmSlug });
            const licenseKey = gen?.data?.data?.license_key;

            if (licenseKey) {
              // Tek domain: vaktinibildir.com.tr
              await api.post('/licenses/verify', {
                license_key: licenseKey,
                site_url: 'https://vaktinibildir.com.tr',
                site_name: 'VaktiniBildir'
              });

              window.alert(`✅ VaktiniBildir lisans kodu hazır!\n\nFirma slug: ${firmSlug}\nLisans: ${licenseKey}`);
            } else {
              window.alert(`⚠️ Firma oluşturuldu ama lisans üretilemedi. Slug: ${firmSlug}`);
            }
          } catch (licErr) {
            console.error('VaktiniBildir lisans/provision hatası:', licErr);
            window.alert(`⚠️ Firma oluşturuldu ama lisans/provision adımında hata var. Slug: ${firmSlug}`);
          }
        }
      }
      
      if (res.data) {
        if (editingFirm) {
          // Refresh the firms list
          const updatedRes = await api.get('/firms');
          setFirms(Array.isArray(updatedRes.data.data) ? updatedRes.data.data : []);
          setSuccess('Firma başarıyla güncellendi.');
        } else {
          // Refresh the firms list
          const updatedRes = await api.get('/firms');
          setFirms(Array.isArray(updatedRes.data.data) ? updatedRes.data.data : []);
          setSuccess('Firma başarıyla eklendi.');
        }
        handleCloseModal();
      }
    } catch (err) {
      setError(err?.error || err?.message || 'Bilinmeyen hata');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (firmId) => {
    if (!window.confirm('Bu firmayı silmek istediğinizden emin misiniz?')) return;
    try {
              await api.delete(`/firms/${firmId}`);
        setFirms(prev => prev.filter(f => f.id !== firmId));
        setSuccess('Firma başarıyla silindi.');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleConnect = async (firm) => {
    try {
      setLoading(true);
      // Admin login token al
      const response = await api.get(`/firms/${firm.slug}/admin-login`);
      
      if (response.data.success) {
        // Yeni sekmede client panel'e yönlendir
        const loginUrl = `${window.location.origin}/login?admin_token=${response.data.token}&slug=${firm.slug}&expires=${response.data.expiresAt}`;
        window.open(loginUrl, '_blank');
        setSuccess(`${firm.name} hesabına bağlanılıyor...`);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Bağlantı token\'ı oluşturulamadı: ' + (response.data.error || 'Bilinmeyen hata'));
      }
    } catch (err) {
      console.error('Bağlan hatası:', err);
      setError('Bağlantı hatası: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleBrowserRestart = async (firm) => {
    if (!firm?.slug) {
      setError('Firma slug bilgisi bulunamadı');
      return;
    }

    const confirmed = window.confirm(
      `${firm.name || firm.slug} için WhatsApp tarayıcısı yeniden başlatılsın mı?\n\nBu işlem mevcut oturumu kısa süreli kesebilir.`
    );

    if (!confirmed) return;

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const response = await api.post(`/whatsapp/${firm.slug}/restart`);

      if (!response?.data?.success) {
        throw new Error(response?.data?.message || 'Tarayıcı yeniden başlatılamadı');
      }

      setSuccess(`${firm.name || firm.slug} için tarayıcı yeniden başlatıldı`);
      await refreshAllSystemData();
    } catch (err) {
      console.error('Tarayıcı restart hatası:', err);
      setError(err.response?.data?.message || err.response?.data?.error || err.message || 'Tarayıcı yeniden başlatılırken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // WhatsApp QR sayfasını aç
  const openQRPage = (slug) => {
    // Yeni WhatsApp service QR endpoint'ini kullan
    const url = `https://panel.fbajans.com/api/whatsapp-service/qr-image/${slug}`;
    window.open(url, '_blank');
  };

  // Get WhatsApp status color and text (Sunucudaki servis durumu)
  const getWhatsAppStatus = (status) => {
    switch(status) {
      case 'connected': 
      case 'success': 
        return { color: '#28a745', text: '✅ Bağlı', icon: '✅', needsQR: false, action: 'connected' };
      case 'qr_waiting': 
      case 'warning': 
        return { color: '#ffc107', text: '🟡 QR Bekliyor', icon: '🟡', needsQR: true, action: 'qr_waiting' };
      case 'disconnected': 
      case 'error': 
      case 'timeout': 
      case 'servis_hatası': 
        return { color: '#dc3545', text: '❌ Erişilemiyor', icon: '❌', needsQR: true, action: 'disconnected' };
      case 'service_unavailable': 
        return { color: '#6c757d', text: '🔴 Servis Kapalı', icon: '🔴', needsQR: false, action: 'service_unavailable' };
                   case 'not_initialized': 
      case 'initializing': 
        return { color: '#ffc107', text: '🟡 QR Bekliyor', icon: '🟡', needsQR: true, action: 'qr_waiting' };
      case 'authenticated':
      case 'ready':
        return { color: '#28a745', text: '✅ Bağlı', icon: '✅', needsQR: false, action: 'connected' };
      default: 
        return { color: '#6c757d', text: '⏸️ Test Edilmedi', icon: '⏸️', needsQR: false, action: 'unknown' };
    }
  };

  // WhatsApp durumu kontrol fonksiyonu
  const checkWhatsAppStatus = async (slug) => {
    try {
      const response = await fetch(`/api/whatsapp-service/status/${slug}`);
      const data = await response.json();
      
      if (data.success) {
        const status = data.data.data.status || data.data.status;
        console.log(`WhatsApp Status for ${slug}:`, status);
        // Durumu güncelle
        refreshAllSystemData();
      }
    } catch (error) {
      console.error('WhatsApp status hatası:', error);
    }
  };

  // Get WhatsApp site access status (WhatsApp sitesine erişim durumu)
  const getWhatsAppSiteStatus = (firm) => {
    // Backend'den gelen live_status veya whatsapp_status'u kullan
    const status = firm.live_status || firm.whatsapp_status;
    
    if (status) {
      switch(status) {
        case 'connected':
        case 'success':
        case 'authenticated':
          return { color: '#28a745', text: '🌐 Erişilebilir', icon: '🌐' };
        case 'qr_waiting':
        case 'warning':
          return { color: '#ffc107', text: '🌐 Erişilebilir', icon: '🌐' };
        case 'error':
        case 'timeout':
        case 'servis_hatası':
        case 'disconnected':
          return { color: '#dc3545', text: '❌ Erişilemez', icon: '❌' };
        case 'initializing':
          return { color: '#17a2b8', text: '🔄 Test Ediliyor', icon: '🔄' };
        default:
          return { color: '#6c757d', text: '⏸️ Test Edilmedi', icon: '⏸️' };
      }
    }
    
    // Eğer status yoksa, proxy durumuna göre tahmin et
    if (firm.proxy_host && firm.proxy_port) {
      return { color: '#17a2b8', text: '🔵 Proxy ile', icon: '🔵' };
    }
    
    return { color: '#6c757d', text: '❓ Bilinmiyor', icon: '❓' };
  };

     // Get WhatsApp action button - Sadece durum kontrolü
   const getWhatsAppActionButton = (firm) => {
     return (
       <button
         onClick={() => handleWhatsAppAction(firm.slug, 'status')}
         style={{
           background: '#17a2b8',
           color: 'white',
           border: 'none',
           padding: '2px 6px',
           borderRadius: '4px',
           fontSize: '9px',
           cursor: 'pointer',
           width: '100%'
         }}
       >
         <i className="fas fa-mobile-alt" style={{ marginRight: '8px' }}></i>
         Durum
       </button>
     );
   };

  // Handle WhatsApp actions - Sadece durum kontrolü
  const handleWhatsAppAction = async (slug, action) => {
    try {
      setLoading(true);
      
      // Doğru slug ile durum kontrolü yap
      const response = await fetch(`/api/whatsapp-service/status/${slug}`);
      const data = await response.json();
      
      if (data.success) {
        const status = data.data.data.status || data.data.status;
        console.log(`WhatsApp Status for ${slug}:`, status);
        
        // Duruma göre mesaj
        switch(status) {
          case 'connected':
          case 'authenticated':
            setSuccess('✅ WhatsApp bağlı!');
            break;
          case 'qr_waiting':
            setSuccess('🟡 QR kod bekliyor!');
            break;
          case 'disconnected':
          case 'timeout':
          case 'servis_hatası':
            setError('❌ WhatsApp bağlantısı kesildi!');
            break;
          default:
            setSuccess(`📱 WhatsApp durumu: ${status}`);
        }
        
        // Firmaları yenile
        setTimeout(() => {
          refreshAllSystemData();
        }, 1000);
      } else {
        setError('WhatsApp durumu alınamadı!');
      }
      
    } catch (err) {
      setError(`WhatsApp durumu kontrol edilemedi: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

     // Get Proxy status color and text
   const getProxyStatus = (firm) => {
     // Proxy bilgileri yoksa
     if (!firm.proxy_host || !firm.proxy_port) {
       return { color: '#6c757d', text: '⚪ Tanımlanmamış' };
     }
     
     // Proxy bilgileri varsa direkt aktif kabul et
     return { color: '#28a745', text: '🟢 Aktif' };
   };

   // Filtrelenmiş firmalar
   const filteredFirms = useMemo(() => {
    return firms.filter(firm => {
      // Arama filtresi
      if (filters.search && !firm.name?.toLowerCase().includes(filters.search.toLowerCase()) && 
          !firm.slug?.toLowerCase().includes(filters.search.toLowerCase())) {
        return false;
      }
      
      // Durum filtresi
      if (filters.status !== 'all' && firm.status !== filters.status) {
        return false;
      }
      
      // WhatsApp durumu filtresi
      if (filters.whatsappStatus !== 'all') {
        const whatsappStatus = getWhatsAppStatus(firm.whatsapp_status);
        if (whatsappStatus.action !== filters.whatsappStatus) {
          return false;
        }
      }
      
      // Proxy durumu filtresi
      if (filters.proxyStatus !== 'all') {
        const proxyStatus = getProxyStatus(firm);
        if (proxyStatus.text.includes('Aktif') && filters.proxyStatus !== 'working') return false;
        if (proxyStatus.text.includes('Hata') && filters.proxyStatus !== 'failed') return false;
        if (proxyStatus.text.includes('Tanımlanmamış') && filters.proxyStatus !== 'undefined') return false;
      }
      
      // Lisans durumu filtresi
      if (filters.licenseStatus !== 'all') {
        if (firm.license_expire) {
          const expireDate = new Date(firm.license_expire);
          const now = new Date();
          const daysLeft = Math.ceil((expireDate - now) / (1000 * 60 * 60 * 24));
          
          if (filters.licenseStatus === 'expired' && daysLeft >= 0) return false;
          if (filters.licenseStatus === 'active' && daysLeft < 0) return false;
          if (filters.licenseStatus === 'warning' && (daysLeft < 0 || daysLeft > 30)) return false;
        }
      }
      
      return true;
    });
  }, [firms, filters]);

     // İstatistikleri hesapla
   const calculateStats = useMemo(() => {
     const newStats = {
       total: firms.length,
       active: firms.filter(f => f.status === 'active').length,
       inactive: firms.filter(f => f.status === 'inactive').length,
       whatsappConnected: firms.filter(f => getWhatsAppStatus(f.whatsapp_status).action === 'connected').length,
       whatsappWaiting: firms.filter(f => getWhatsAppStatus(f.whatsapp_status).action === 'qr_waiting').length,
       proxyWorking: firms.filter(f => {
         const proxyStatus = getProxyStatus(f);
         return proxyStatus.text.includes('Aktif');
       }).length,
       licenseExpired: firms.filter(f => {
         if (f.license_expire) {
           const expireDate = new Date(f.license_expire);
           const now = new Date();
           return expireDate < now;
         }
         return false;
       }).length
     };
     setStats(newStats);
     return newStats;
   }, [firms]);

  // Mobile Card Component
  const FirmCard = ({ firm }) => {
    const whatsappStatus = getWhatsAppStatus(firm.whatsapp_status);

    return (
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid #e0e0e0'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
          <div style={{ flex: 1 }}>
            <h3 style={{ 
              margin: '0 0 4px 0', 
              fontSize: '16px', 
              fontWeight: 'bold', 
              color: '#333' 
            }}>
              {firm.name || `Firma #${firm.id}`}
            </h3>
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              background: '#f5f5f5', 
              padding: '2px 8px', 
              borderRadius: '4px', 
              display: 'inline-block',
              marginBottom: '8px',
              fontFamily: 'monospace'
            }}>
              {firm.slug}
            </div>
          </div>
                     <div style={{
             background: firm.status === 'active' ? '#28a745' : '#dc3545',
             color: 'white',
             padding: '4px 8px',
             borderRadius: '12px',
             fontSize: '11px',
             fontWeight: 'bold'
           }}>
             {firm.status === 'active' ? 'Aktif' : 'Pasif'}
           </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
          <div>
            <div style={{ fontSize: '11px', color: '#888' }}>Lisans Bitiş</div>
            <div style={{ fontSize: '12px', color: '#333' }}>
              {firm.license_expire ? new Date(firm.license_expire).toLocaleDateString('tr-TR') : 'Belirtilmemiş'}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '11px', color: '#888' }}>Mesaj Kotası</div>
            <div style={{ fontSize: '12px', color: '#333' }}>
              {firm.messages_sent || 0} / {firm.message_limit || 0}
            </div>
          </div>
        </div>

        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontSize: '11px', color: '#888' }}>WhatsApp Durumu</div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <div style={{ 
              fontSize: '12px', 
              color: 'white',
              background: whatsappStatus.color,
              padding: '4px 8px', 
              borderRadius: '12px', 
              display: 'inline-block',
              fontWeight: 'bold'
            }}>
              {whatsappStatus.text}
            </div>
            {getWhatsAppActionButton(firm)}
          </div>
        </div>

        {firm.proxy_host && (
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '11px', color: '#888' }}>Proxy</div>
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              background: '#f8f9fa', 
              padding: '4px 8px', 
              borderRadius: '4px', 
              fontFamily: 'monospace'
            }}>
              {firm.proxy_host}:{firm.proxy_port}
            </div>
            <div style={{ marginTop: '4px' }}>
              {(() => {
                const proxyStatus = getProxyStatus(firm);
                return (
                  <span style={{
                    background: proxyStatus.color,
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '8px',
                    fontSize: '10px',
                    fontWeight: 'bold'
                  }}>
                    {proxyStatus.text}
                  </span>
                );
              })()}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={() => handleConnect(firm)}
            style={{
              flex: 1,
              background: '#28a745',
              color: 'white',
              border: 'none',
              padding: '8px 12px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              minWidth: '100px'
            }}
            title="Firma hesabına bağlan"
          >
            <i className="fas fa-sign-in-alt" style={{ marginRight: '8px' }}></i>
            Bağlan
          </button>
          <button
            onClick={() => handleBrowserRestart(firm)}
            style={{
              flex: 1,
              background: '#fd7e14',
              color: 'white',
              border: 'none',
              padding: '8px 12px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
            title="WhatsApp tarayıcısını yeniden başlat"
          >
            <i className="fas fa-redo" style={{ marginRight: '8px' }}></i>
            Restart
          </button>
          <button
            onClick={() => openScreenshotLink(firm.slug)}
            style={{
              flex: 1,
              background: '#6f42c1',
              color: 'white',
              border: 'none',
              padding: '8px 12px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
            title="Screenhost bağlantısını aç"
          >
            <i className="fas fa-camera" style={{ marginRight: '8px' }}></i>
            Screenhost
          </button>
          <button
            onClick={() => handleOpenModal(firm)}
            style={{
              flex: 1,
              background: '#007bff',
              color: 'white',
              border: 'none',
              padding: '8px 12px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            <i className="fas fa-edit" style={{ marginRight: '8px' }}></i>
            Düzenle
          </button>
          <button
            onClick={() => handleDelete(firm.id)}
            style={{
              flex: 1,
              background: '#dc3545',
              color: 'white',
              border: 'none',
              padding: '8px 12px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            <i className="fas fa-trash" style={{ marginRight: '8px' }}></i>
            Sil
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ padding: isMobile ? '15px' : '30px' }}>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
            Firmalar yükleniyor...
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={{ padding: isMobile ? '15px' : '30px', overflowX: 'hidden' }}>
        {/* Standart Header */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '30px',
          marginBottom: '30px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '20px' : '0'
          }}>
            <div>
              <h1 style={{ 
                margin: '0 0 5px', 
                fontSize: isMobile ? '24px' : '32px', 
                fontWeight: '700',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                FBAJANS WhatsApp Business
              </h1>
              <p style={{ 
                margin: '0 0 5px', 
                fontSize: isMobile ? '16px' : '18px',
                fontWeight: '500',
                color: '#555'
              }}>
                <i className="fas fa-building" style={{ marginRight: '8px' }}></i>
                Firma Yönetim Paneli
              </p>
              <p style={{ 
                margin: '5px 0 0 0', 
                fontSize: isMobile ? '13px' : '14px',
                color: '#999'
              }}>
                Toplam {stats.total} firma • {stats.active} aktif • {stats.inactive} pasif
              </p>
            </div>
            <div style={{
              display: 'flex',
              gap: '15px',
              flexWrap: 'wrap',
              justifyContent: isMobile ? 'center' : 'flex-end',
              flexDirection: isMobile ? 'column' : 'row',
              width: isMobile ? '100%' : 'auto'
            }}>
              <button
                onClick={refreshAllSystemData}
                disabled={refreshingWhatsApp}
                style={{
                  padding: '12px 20px',
                  background: '#f8f9fa',
                  border: '1px solid #e2e8f0',
                  borderRadius: '10px',
                  color: '#333',
                  cursor: refreshingWhatsApp ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.3s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: refreshingWhatsApp ? 0.6 : 1,
                  width: isMobile ? '100%' : 'auto',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  if (!refreshingWhatsApp) {
                    e.target.style.background = '#e9ecef';
                    e.target.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!refreshingWhatsApp) {
                    e.target.style.background = '#f8f9fa';
                    e.target.style.transform = 'translateY(0)';
                  }
                }}
              >
                <i className="fas fa-sync-alt" style={{ marginRight: '8px' }}></i>
                {refreshingWhatsApp ? 'Güncelleniyor...' : 'Yenile'}
              </button>
              <button
                onClick={() => handleOpenModal()}
                style={{
                  padding: '12px 20px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  borderRadius: '10px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.3s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: isMobile ? '100%' : 'auto',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 5px 15px rgba(102, 126, 234, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <i className="fas fa-plus" style={{ marginRight: '8px' }}></i>
                Yeni Firma
              </button>
            </div>
           </div>
         </div>

         {/* İstatistik Kartları */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '30px'
        }}>
           <div style={{
             background: 'white',
             borderRadius: '16px',
             padding: '24px',
             boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
             border: '1px solid #f0f0f0',
             textAlign: 'center',
             transition: 'transform 0.3s ease',
             cursor: 'pointer'
           }}
           onMouseEnter={(e) => e.target.style.transform = 'translateY(-5px)'}
           onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
           onClick={() => setFilters(prev => ({ ...prev, status: 'active' }))}>
             <i className="fas fa-check-circle" style={{ fontSize: '32px', marginBottom: '8px', color: '#28a745' }}></i>
             <div style={{ fontSize: '24px', fontWeight: '700', color: '#28a745', marginBottom: '4px' }}>{stats.active}</div>
             <div style={{ fontSize: '14px', color: '#666' }}>Aktif Firma</div>
           </div>

           <div style={{
             background: 'white',
             borderRadius: '16px',
             padding: '24px',
             boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
             border: '1px solid #f0f0f0',
             textAlign: 'center',
             transition: 'transform 0.3s ease',
             cursor: 'pointer'
           }}
           onMouseEnter={(e) => e.target.style.transform = 'translateY(-5px)'}
           onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
           onClick={() => setFilters(prev => ({ ...prev, whatsappStatus: 'connected' }))}>
             <i className="fas fa-mobile-alt" style={{ fontSize: '32px', marginBottom: '8px', color: '#17a2b8' }}></i>
             <div style={{ fontSize: '24px', fontWeight: '700', color: '#17a2b8', marginBottom: '4px' }}>{stats.whatsappConnected}</div>
             <div style={{ fontSize: '14px', color: '#666' }}>WhatsApp Bağlı</div>
           </div>

           <div style={{
             background: 'white',
             borderRadius: '16px',
             padding: '24px',
             boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
             border: '1px solid #f0f0f0',
             textAlign: 'center',
             transition: 'transform 0.3s ease',
             cursor: 'pointer'
           }}
           onMouseEnter={(e) => e.target.style.transform = 'translateY(-5px)'}
           onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
           onClick={() => setFilters(prev => ({ ...prev, proxyStatus: 'working' }))}>
             <i className="fas fa-globe" style={{ fontSize: '32px', marginBottom: '8px', color: '#28a745' }}></i>
             <div style={{ fontSize: '24px', fontWeight: '700', color: '#28a745', marginBottom: '4px' }}>{stats.proxyWorking}</div>
             <div style={{ fontSize: '14px', color: '#666' }}>Proxy Çalışıyor</div>
           </div>

           <div style={{
             background: 'white',
             borderRadius: '16px',
             padding: '24px',
             boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
             border: '1px solid #f0f0f0',
             textAlign: 'center',
             transition: 'transform 0.3s ease',
             cursor: 'pointer'
           }}
           onMouseEnter={(e) => e.target.style.transform = 'translateY(-5px)'}
           onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
           onClick={() => setFilters(prev => ({ ...prev, licenseStatus: 'expired' }))}>
             <i className="fas fa-exclamation-triangle" style={{ fontSize: '32px', marginBottom: '8px', color: '#dc3545' }}></i>
             <div style={{ fontSize: '24px', fontWeight: '700', color: '#dc3545', marginBottom: '4px' }}>{stats.licenseExpired}</div>
             <div style={{ fontSize: '14px', color: '#666' }}>Lisans Süresi Dolmuş</div>
           </div>
         </div>

                 {/* Filtreleme Bölümü */}
         <div style={{
           background: 'white',
           borderRadius: '16px',
           padding: '24px',
           marginBottom: '24px',
           boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
           border: '1px solid #f0f0f0'
         }}>
           <div style={{
             display: 'flex',
             alignItems: 'center',
             gap: '12px',
             marginBottom: '20px'
           }}>
             <i className="fas fa-search" style={{ fontSize: '20px' }}></i>
             <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#333' }}>Filtreleme & Arama</h3>
           </div>
           
           <div style={{
             display: 'grid',
             gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
             gap: '16px'
           }}>
             {/* Arama */}
             <div>
               <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#555' }}>
                 🔎 Firma Ara
               </label>
               <input
                 type="text"
                 placeholder="Firma adı veya slug..."
                 value={filters.search}
                 onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                 style={{
                   width: '100%',
                   padding: '12px 16px',
                   border: '2px solid #e0e0e0',
                   borderRadius: '12px',
                  fontSize: '14px',
                  transition: 'border-color 0.3s ease',
                  boxSizing: 'border-box'
                 }}
                 onFocus={(e) => e.target.style.borderColor = '#667eea'}
                 onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
               />
             </div>

             {/* Durum Filtresi */}
             <div>
               <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#555' }}>
                 <i className="fas fa-chart-bar" style={{ marginRight: '8px' }}></i>
                 Durum
               </label>
               <select
                 value={filters.status}
                 onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                 style={{
                   width: '100%',
                   padding: '12px 16px',
                   border: '2px solid #e0e0e0',
                   borderRadius: '12px',
                  fontSize: '14px',
                  background: 'white',
                  cursor: 'pointer',
                  boxSizing: 'border-box'
                 }}
               >
                 <option value="all">Tümü</option>
                 <option value="active">Aktif</option>
                 <option value="inactive">Pasif</option>
               </select>
             </div>

             {/* WhatsApp Durumu */}
             <div>
               <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#555' }}>
                 <i className="fas fa-mobile-alt" style={{ marginRight: '8px' }}></i>
                 WhatsApp
               </label>
               <select
                 value={filters.whatsappStatus}
                 onChange={(e) => setFilters(prev => ({ ...prev, whatsappStatus: e.target.value }))}
                 style={{
                   width: '100%',
                   padding: '12px 16px',
                   border: '2px solid #e0e0e0',
                   borderRadius: '12px',
                  fontSize: '14px',
                  background: 'white',
                  cursor: 'pointer',
                  boxSizing: 'border-box'
                 }}
               >
                 <option value="all">Tümü</option>
                 <option value="connected">Bağlı</option>
                 <option value="qr_waiting">QR Bekliyor</option>
                 <option value="disconnected">Bağlantısız</option>
                 <option value="not_initialized">Başlatılmamış</option>
               </select>
             </div>

             {/* Proxy Durumu */}
             <div>
               <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#555' }}>
                 <i className="fas fa-globe" style={{ marginRight: '8px' }}></i>
                 Proxy
               </label>
               <select
                 value={filters.proxyStatus}
                 onChange={(e) => setFilters(prev => ({ ...prev, proxyStatus: e.target.value }))}
                 style={{
                   width: '100%',
                   padding: '12px 16px',
                   border: '2px solid #e0e0e0',
                   borderRadius: '12px',
                  fontSize: '14px',
                  background: 'white',
                  cursor: 'pointer',
                  boxSizing: 'border-box'
                 }}
               >
                 <option value="all">Tümü</option>
                 <option value="working">Çalışıyor</option>
                 <option value="failed">Hata</option>
                 <option value="undefined">Tanımlanmamış</option>
               </select>
             </div>

             {/* Lisans Durumu */}
             <div>
               <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#555' }}>
                 📜 Lisans
               </label>
               <select
                 value={filters.licenseStatus}
                 onChange={(e) => setFilters(prev => ({ ...prev, licenseStatus: e.target.value }))}
                 style={{
                   width: '100%',
                   padding: '12px 16px',
                   border: '2px solid #e0e0e0',
                   borderRadius: '12px',
                  fontSize: '14px',
                  background: 'white',
                  cursor: 'pointer',
                  boxSizing: 'border-box'
                 }}
               >
                 <option value="all">Tümü</option>
                 <option value="active">Aktif</option>
                 <option value="expired">Süresi Dolmuş</option>
                 <option value="warning">Uyarı (30 gün)</option>
               </select>
             </div>
           </div>

           {/* Filtreleri Temizle */}
           <div style={{ marginTop: '20px', textAlign: 'center' }}>
             <button
               onClick={() => setFilters({
                 search: '',
                 status: 'all',
                 whatsappStatus: 'all',
                 proxyStatus: 'all',
                 licenseStatus: 'all'
               })}
               style={{
                 background: '#f8f9fa',
                 color: '#6c757d',
                 border: '1px solid #dee2e6',
                 padding: '10px 20px',
                 borderRadius: '8px',
                 fontSize: '14px',
                 fontWeight: '500',
                 cursor: 'pointer',
                 transition: 'all 0.3s ease'
               }}
               onMouseEnter={(e) => e.target.style.background = '#e9ecef'}
               onMouseLeave={(e) => e.target.style.background = '#f8f9fa'}
             >
               <i className="fas fa-trash-alt" style={{ marginRight: '8px' }}></i>
               Filtreleri Temizle
             </button>
           </div>
         </div>

         {/* Sonuç Sayısı */}
         <div style={{
           display: 'flex',
           justifyContent: 'space-between',
           alignItems: 'center',
           marginBottom: '20px',
           padding: '0 8px'
         }}>
           <div style={{ fontSize: '16px', fontWeight: '600', color: '#333' }}>
             <i className="fas fa-clipboard-list" style={{ marginRight: '8px' }}></i>
             Sonuçlar: {filteredFirms.length} firma
           </div>
           <div style={{ fontSize: '14px', color: '#666' }}>
             Toplam {firms.length} firmadan filtrelendi
           </div>
         </div>

         {/* Success/Error Messages */}
         {success && (
          <div style={{
            background: '#d4edda',
            color: '#155724',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid #c3e6cb'
          }}>
            <i className="fas fa-check-circle" style={{ marginRight: '8px' }}></i>
            {success}
          </div>
        )}

        {error && (
          <div style={{
            background: '#f8d7da',
            color: '#721c24',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid #f5c6cb'
          }}>
            <i className="fas fa-exclamation-circle" style={{ marginRight: '8px' }}></i>
            {error}
          </div>
        )}

        {/* Firms List */}
        {isMobile ? (
          // Mobile Card View
          <div>
            {firms.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: '#666',
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <i className="fas fa-chart-bar" style={{ fontSize: '48px', marginBottom: '16px', color: '#6c757d' }}></i>
                <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>Henüz firma bulunmuyor</div>
                <div style={{ fontSize: '14px' }}>İlk firmayı eklemek için yukarıdaki butona tıklayın</div>
              </div>
            ) : (
              Array.isArray(firms) ? firms.map(firm => (
                <FirmCard key={firm.id} firm={firm} />
              )) : []
            )}
          </div>
        ) : (
          // Desktop Table View
          <div style={{
            background: 'white',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                             <thead>
                 <tr style={{ background: '#f8f9fa' }}>
                   <th style={{ padding: '16px', textAlign: 'left', borderBottom: '2px solid #dee2e6', color: '#495057', fontWeight: '600' }}>ID</th>
                   <th style={{ padding: '16px', textAlign: 'left', borderBottom: '2px solid #dee2e6', color: '#495057', fontWeight: '600' }}>Name</th>
                   <th style={{ padding: '16px', textAlign: 'left', borderBottom: '2px solid #dee2e6', color: '#495057', fontWeight: '600' }}>Slug</th>
                   <th style={{ padding: '16px', textAlign: 'left', borderBottom: '2px solid #dee2e6', color: '#495057', fontWeight: '600' }}>Lisans Günü</th>
                   <th style={{ padding: '16px', textAlign: 'left', borderBottom: '2px solid #dee2e6', color: '#495057', fontWeight: '600' }}>Mesaj Limiti</th>
                   <th style={{ padding: '16px', textAlign: 'left', borderBottom: '2px solid #dee2e6', color: '#495057', fontWeight: '600' }}>WhatsApp</th>
                   <th style={{ padding: '16px', textAlign: 'left', borderBottom: '2px solid #dee2e6', color: '#495057', fontWeight: '600' }}>Proxy</th>
                   <th style={{ padding: '16px', textAlign: 'left', borderBottom: '2px solid #dee2e6', color: '#495057', fontWeight: '600' }}>Proxy Durumu</th>
                   <th style={{ padding: '16px', textAlign: 'left', borderBottom: '2px solid #dee2e6', color: '#495057', fontWeight: '600' }}>WhatsApp Web</th>
                    <th style={{ padding: '16px', textAlign: 'left', borderBottom: '2px solid #dee2e6', color: '#495057', fontWeight: '600' }}>Durum</th>
                                        <th style={{ padding: '16px', textAlign: 'center', borderBottom: '2px solid #dee2e6', color: '#495057', fontWeight: '600' }}>İşlemler</th>
                 </tr>
               </thead>
              <tbody>
                {firms.length === 0 ? (
                                                        <tr>
                     <td colSpan="13" style={{ 
                       padding: '40px', 
                       textAlign: 'center', 
                       color: '#666',
                       fontSize: '16px'
                     }}>
                      <i className="fas fa-chart-bar" style={{ fontSize: '48px', marginBottom: '16px', color: '#6c757d' }}></i>
                      <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Henüz firma bulunmuyor</div>
                      <div>İlk firmayı eklemek için "Yeni Firma" butonuna tıklayın</div>
                    </td>
                  </tr>
                                 ) : (
                   Array.isArray(filteredFirms) ? filteredFirms.map((firm, index) => {
                    const whatsappStatus = getWhatsAppStatus(firm.whatsapp_status);
                    return (
                      <tr key={firm.id} style={{ 
                        borderBottom: '1px solid #dee2e6',
                        background: index % 2 === 0 ? 'white' : '#f8f9fa'
                      }}>
                        <td style={{ padding: '16px', color: '#495057' }}>{firm.id}</td>
                        <td style={{ padding: '16px', color: '#495057' }} title={firm.name || '-'}>
                          {firm.name && firm.name.length > 20 ? `${firm.name.slice(0, 20)}..` : (firm.name || '-')}
                        </td>
                        <td style={{ padding: '16px', color: '#495057' }}>
                          <span style={{
                            background: '#f8f9fa',
                            color: '#495057',
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontFamily: 'monospace'
                          }}>
                            {firm.slug}
                          </span>
                        </td>
                                                 <td style={{ padding: '16px', color: '#495057' }}>
                           {(() => {
                             if (firm.license_expire) {
                               const expireDate = new Date(firm.license_expire);
                               const now = new Date();
                               const daysLeft = Math.ceil((expireDate - now) / (1000 * 60 * 60 * 24));
                               
                               if (daysLeft < 0) {
                                 return (
                                   <span style={{
                                     background: '#dc3545',
                                     color: 'white',
                                     padding: '4px 8px',
                                     borderRadius: '12px',
                                     fontSize: '11px',
                                     fontWeight: 'bold'
                                   }}>
                                     <i className="fas fa-exclamation-triangle" style={{ marginRight: '8px' }}></i>
                                     Süresi Dolmuş
                                   </span>
                                 );
                               } else if (daysLeft <= 3) {
                                 return (
                                   <span style={{
                                     background: '#dc3545',
                                     color: 'white',
                                     padding: '4px 8px',
                                     borderRadius: '12px',
                                     fontSize: '11px',
                                     fontWeight: 'bold'
                                   }}>
                                     ⚠️ {daysLeft} Gün
                                   </span>
                                 );
                               } else if (daysLeft <= 7) {
                                 return (
                                   <span style={{
                                     background: '#ffc107',
                                     color: 'white',
                                     padding: '4px 8px',
                                     borderRadius: '12px',
                                     fontSize: '11px',
                                     fontWeight: 'bold'
                                   }}>
                                     ⚠️ {daysLeft} Gün
                                   </span>
                                 );
                               } else {
                                 return (
                                   <span style={{
                                     background: '#28a745',
                                     color: 'white',
                                     padding: '4px 8px',
                                     borderRadius: '12px',
                                     fontSize: '11px',
                                     fontWeight: 'bold'
                                   }}>
                                     ✅ {daysLeft} Gün
                                   </span>
                                 );
                               }
                             } else {
                               return (
                                 <span style={{
                                   background: '#6c757d',
                                   color: 'white',
                                   padding: '4px 8px',
                                   borderRadius: '12px',
                                   fontSize: '11px',
                                   fontWeight: 'bold'
                                 }}>
                                   ❓ Belirtilmemiş
                                 </span>
                                 );
                             }
                           })()}
                         </td>
                        <td style={{ padding: '16px', color: '#495057' }}>{firm.messages_sent || 0} / {firm.message_limit || 0}</td>
                                                                          <td style={{ padding: '16px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <span style={{
                                background: whatsappStatus.color,
                                color: 'white',
                                padding: '4px 8px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: 'bold',
                                textAlign: 'center'
                              }}>
                                {whatsappStatus.text}
                              </span>
                              <button
                                onClick={() => handleWhatsAppAction(firm.slug, 'status')}
                                style={{
                                  background: '#17a2b8',
                                  color: 'white',
                                  border: 'none',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  fontSize: '9px',
                                  cursor: 'pointer',
                                  width: '100%'
                                }}
                              >
                                <i className="fas fa-mobile-alt" style={{ marginRight: '8px' }}></i>
         Durum
                              </button>
                            </div>
                          </td>
                        <td style={{ padding: '16px', color: '#495057' }}>
                          {firm.proxy_host ? `${firm.proxy_host}:${firm.proxy_port}` : '-'}
                        </td>
                        <td style={{ padding: '16px' }}>
                          {(() => {
                            const proxyStatus = getProxyStatus(firm);
                            return (
                              <span style={{
                                background: proxyStatus.color,
                                color: 'white',
                                padding: '4px 8px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: 'bold'
                              }}>
                                {proxyStatus.text}
                              </span>
                            );
                          })()}
                        </td>
                        <td style={{ padding: '16px' }}>
                          {(() => {
                            const siteStatus = getWhatsAppSiteStatus(firm);
                            return (
                              <span style={{
                                background: siteStatus.color,
                                color: 'white',
                                padding: '4px 8px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: 'bold'
                              }}>
                                {siteStatus.text}
                              </span>
                            );
                          })()}
                        </td>
                        <td style={{ padding: '16px' }}>
                                                     <span style={{
                             background: firm.status === 'active' ? '#d4edda' : '#f8d7da',
                             color: firm.status === 'active' ? '#155724' : '#721c24',
                             padding: '4px 8px',
                             borderRadius: '12px',
                             fontSize: '11px',
                             fontWeight: 'bold'
                           }}>
                             {firm.status === 'active' ? '✅ Aktif' : '❌ Pasif'}
                           </span>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <div
                              style={{ position: 'relative' }}
                              onMouseEnter={() => setHoveredAction(`${firm.id}-connect`)}
                              onMouseLeave={() => setHoveredAction(null)}
                            >
                              {hoveredAction === `${firm.id}-connect` && (
                                <div style={{
                                  position: 'absolute',
                                  bottom: 'calc(100% + 6px)',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  background: '#212529',
                                  color: 'white',
                                  padding: '4px 8px',
                                  borderRadius: '6px',
                                  fontSize: '11px',
                                  fontWeight: '500',
                                  whiteSpace: 'nowrap',
                                  zIndex: 20,
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                                }}>
                                  Bağlan
                                </div>
                              )}
                              <button
                                onClick={() => handleConnect(firm)}
                                style={{
                                  background: '#28a745',
                                  color: 'white',
                                  border: 'none',
                                  width: '34px',
                                  height: '30px',
                                  borderRadius: '4px',
                                  fontSize: '13px',
                                  cursor: 'pointer'
                                }}
                                aria-label="Bağlan"
                              >
                                <i className="fas fa-sign-in-alt"></i>
                              </button>
                            </div>

                            <div
                              style={{ position: 'relative' }}
                              onMouseEnter={() => setHoveredAction(`${firm.id}-restart`)}
                              onMouseLeave={() => setHoveredAction(null)}
                            >
                              {hoveredAction === `${firm.id}-restart` && (
                                <div style={{
                                  position: 'absolute',
                                  bottom: 'calc(100% + 6px)',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  background: '#212529',
                                  color: 'white',
                                  padding: '4px 8px',
                                  borderRadius: '6px',
                                  fontSize: '11px',
                                  fontWeight: '500',
                                  whiteSpace: 'nowrap',
                                  zIndex: 20,
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                                }}>
                                  Restart
                                </div>
                              )}
                              <button
                                onClick={() => handleBrowserRestart(firm)}
                                style={{
                                  background: '#fd7e14',
                                  color: 'white',
                                  border: 'none',
                                  width: '34px',
                                  height: '30px',
                                  borderRadius: '4px',
                                  fontSize: '13px',
                                  cursor: 'pointer'
                                }}
                                aria-label="Restart"
                              >
                                <i className="fas fa-redo"></i>
                              </button>
                            </div>

                            <div
                              style={{ position: 'relative' }}
                              onMouseEnter={() => setHoveredAction(`${firm.id}-screen`)}
                              onMouseLeave={() => setHoveredAction(null)}
                            >
                              {hoveredAction === `${firm.id}-screen` && (
                                <div style={{
                                  position: 'absolute',
                                  bottom: 'calc(100% + 6px)',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  background: '#212529',
                                  color: 'white',
                                  padding: '4px 8px',
                                  borderRadius: '6px',
                                  fontSize: '11px',
                                  fontWeight: '500',
                                  whiteSpace: 'nowrap',
                                  zIndex: 20,
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                                }}>
                                  Screenhost
                                </div>
                              )}
                              <button
                                onClick={() => openScreenshotLink(firm.slug)}
                                style={{
                                  background: '#6f42c1',
                                  color: 'white',
                                  border: 'none',
                                  width: '34px',
                                  height: '30px',
                                  borderRadius: '4px',
                                  fontSize: '13px',
                                  cursor: 'pointer'
                                }}
                                aria-label="Screenhost Bağlan"
                              >
                                <i className="fas fa-camera"></i>
                              </button>
                            </div>

                            <div
                              style={{ position: 'relative' }}
                              onMouseEnter={() => setHoveredAction(`${firm.id}-edit`)}
                              onMouseLeave={() => setHoveredAction(null)}
                            >
                              {hoveredAction === `${firm.id}-edit` && (
                                <div style={{
                                  position: 'absolute',
                                  bottom: 'calc(100% + 6px)',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  background: '#212529',
                                  color: 'white',
                                  padding: '4px 8px',
                                  borderRadius: '6px',
                                  fontSize: '11px',
                                  fontWeight: '500',
                                  whiteSpace: 'nowrap',
                                  zIndex: 20,
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                                }}>
                                  Düzenle
                                </div>
                              )}
                              <button
                                onClick={() => handleOpenModal(firm)}
                                style={{
                                  background: '#007bff',
                                  color: 'white',
                                  border: 'none',
                                  width: '34px',
                                  height: '30px',
                                  borderRadius: '4px',
                                  fontSize: '13px',
                                  cursor: 'pointer'
                                }}
                                aria-label="Düzenle"
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                            </div>
                              
                            <div
                              style={{ position: 'relative' }}
                              onMouseEnter={() => setHoveredAction(`${firm.id}-delete`)}
                              onMouseLeave={() => setHoveredAction(null)}
                            >
                              {hoveredAction === `${firm.id}-delete` && (
                                <div style={{
                                  position: 'absolute',
                                  bottom: 'calc(100% + 6px)',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  background: '#212529',
                                  color: 'white',
                                  padding: '4px 8px',
                                  borderRadius: '6px',
                                  fontSize: '11px',
                                  fontWeight: '500',
                                  whiteSpace: 'nowrap',
                                  zIndex: 20,
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                                }}>
                                  Sil
                                </div>
                              )}
                              <button
                                onClick={() => handleDelete(firm.id)}
                                style={{
                                  background: '#dc3545',
                                  color: 'white',
                                  border: 'none',
                                  width: '34px',
                                  height: '30px',
                                  borderRadius: '4px',
                                  fontSize: '13px',
                                  cursor: 'pointer'
                                }}
                                aria-label="Sil"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                                          );
                    }) : []
                  )}
              </tbody>
            </table>
          </div>
        )}

        {/* Add/Edit Modal */}
        {showModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: isMobile ? '20px' : '0'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: isMobile ? '20px' : '30px',
              width: isMobile ? '100%' : '600px',
              maxWidth: '100%',
              maxHeight: isMobile ? '90vh' : '80vh',
              overflow: 'auto'
            }}>
              <h2 style={{ 
                margin: '0 0 20px 0', 
                fontSize: isMobile ? '18px' : '20px',
                color: '#333'
              }}>
                {editingFirm ? '✏️ Firma Düzenle' : '➕ Yeni Firma Ekle'}
              </h2>

              <form onSubmit={handleSubmit}>
                {!editingFirm && (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                    gap: '15px',
                    marginBottom: '20px'
                  }}>
                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '5px',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        color: '#333'
                      }}>
                        Entegrasyon Tipi
                      </label>
                      <select
                        value={integrationType}
                        onChange={(e) => {
                          const val = e.target.value;
                          setIntegrationType(val);
                          if (val === 'vaktinibildir') {
                            applyVaktiniBildirDefaults(vbCustomerId);
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '2px solid #e0e0e0',
                          borderRadius: '8px',
                          fontSize: '14px',
                          boxSizing: 'border-box'
                        }}>
                        <option value="opencart">OpenCart</option>
                        <option value="vaktinibildir">VaktiniBildir</option>
                        <option value="webhook">Webhook (ikas/ticimax/shopify)</option>
                      </select>
                    </div>

                    {integrationType === 'vaktinibildir' ? (
                      <div>
                        <label style={{
                          display: 'block',
                          marginBottom: '5px',
                          fontWeight: 'bold',
                          fontSize: '14px',
                          color: '#333'
                        }}>
                          VaktiniBildir Müşteri ID
                        </label>
                        <input
                          type="text"
                          value={vbCustomerId}
                          onChange={(e) => {
                            const v = e.target.value;
                            setVbCustomerId(v);
                            applyVaktiniBildirDefaults(v);
                          }}
                          placeholder="Örn: 12345"
                          style={{
                            width: '100%',
                            padding: '12px',
                            border: '2px solid #e0e0e0',
                            borderRadius: '8px',
                            fontSize: '14px',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                    ) : (
                      integrationType === 'webhook' ? (
                        <div>
                          <label style={{
                            display: 'block',
                            marginBottom: '5px',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            color: '#333'
                          }}>
                            Webhook Provider
                          </label>
                          <select
                            value={webhookProvider}
                            onChange={(e) => setWebhookProvider(e.target.value)}
                            style={{
                              width: '100%',
                              padding: '12px',
                              border: '2px solid #e0e0e0',
                              borderRadius: '8px',
                              fontSize: '14px',
                              boxSizing: 'border-box'
                            }}
                          >
                            <option value="shopify">Shopify</option>
                            <option value="ikas">ikas</option>
                            <option value="ticimax">Ticimax</option>
                          </select>
                        </div>
                      ) : (
                        <div />
                      )
                    )}

                    {integrationType === 'webhook' ? (
                      <div style={{ gridColumn: isMobile ? '1' : '1 / -1' }}>
                        <label style={{
                          display: 'block',
                          marginBottom: '5px',
                          fontWeight: 'bold',
                          fontSize: '14px',
                          color: '#333'
                        }}>
                          Webhook Secret (Shopify için zorunlu)
                        </label>
                        <input
                          type="text"
                          value={webhookSecret}
                          onChange={(e) => setWebhookSecret(e.target.value)}
                          placeholder="Secret"
                          style={{
                            width: '100%',
                            padding: '12px',
                            border: '2px solid #e0e0e0',
                            borderRadius: '8px',
                            fontSize: '14px',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                    ) : null}
                  </div>
                )}

                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
                  gap: '15px',
                  marginBottom: '20px'
                }}>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '5px', 
                      fontWeight: 'bold',
                      fontSize: '14px',
                      color: '#333'
                    }}>
                      Firma Adı *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="FBAJANS Digital Agency"
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '5px', 
                      fontWeight: 'bold',
                      fontSize: '14px',
                      color: '#333'
                    }}>
                      Firma Slug *
                    </label>
                    <input
                      type="text"
                      name="slug"
                      value={formData.slug}
                      onChange={handleInputChange}
                      required
                      placeholder="fbajans"
                      readOnly={!editingFirm && integrationType === 'vaktinibildir'}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '5px', 
                      fontWeight: 'bold',
                      fontSize: '14px',
                      color: '#333'
                    }}>
                      Telefon Numarası
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+90 555 123 45 67"
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '5px', 
                      fontWeight: 'bold',
                      fontSize: '14px',
                      color: '#333'
                    }}>
                      E-posta
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="info@fbajans.com"
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>

                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', 
                  gap: '15px',
                  marginBottom: '20px'
                }}>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '5px', 
                      fontWeight: 'bold',
                      fontSize: '14px',
                      color: '#333'
                    }}>
                      Lisans Başlangıç
                    </label>
                    <input
                      type="date"
                      name="license_start"
                      value={formData.license_start}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                    {/* Hızlı Lisans Süresi Seçim Butonları */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(6, 1fr)',
                      gap: '6px',
                      marginTop: '10px'
                    }}>
                      {[30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330, 365].map((days) => (
                        <button
                          key={days}
                          type="button"
                          onClick={() => {
                            const today = new Date();
                            const currentExpire = formData.license_expire ? new Date(formData.license_expire) : null;
                            const baseDate = currentExpire && !Number.isNaN(currentExpire.getTime()) ? currentExpire : today;
                            const endDate = new Date(baseDate);
                            endDate.setDate(baseDate.getDate() + days);

                            setFormData((prev) => ({
                              ...prev,
                              license_start: prev.license_start || today.toISOString().split('T')[0],
                              license_expire: endDate.toISOString().split('T')[0]
                            }));
                          }}
                          style={{
                            padding: '6px 8px',
                            fontSize: '12px',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            backgroundColor: '#f8f9fa',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            color: '#333',
                            textAlign: 'center'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#e9ecef';
                            e.target.style.borderColor = '#007bff';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#f8f9fa';
                            e.target.style.borderColor = '#ddd';
                          }}
                        >
                          {days}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '5px', 
                      fontWeight: 'bold',
                      fontSize: '14px',
                      color: '#333'
                    }}>
                      Lisans Bitiş
                    </label>
                    <input
                      type="date"
                      name="license_expire"
                      value={formData.license_expire}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '5px', 
                      fontWeight: 'bold',
                      fontSize: '14px',
                      color: '#333'
                    }}>
                      Mesaj Limiti
                    </label>
                    <input
                      type="number"
                      name="message_limit"
                      value={formData.message_limit}
                      onChange={handleInputChange}
                      min="0"
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
              </div>

                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr 1fr', 
                  gap: '15px',
                  marginBottom: '20px'
                }}>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '5px', 
                      fontWeight: 'bold',
                      fontSize: '14px',
                      color: '#333'
                    }}>
                      Proxy Host
                    </label>
                    <input
                      type="text"
                      name="proxy_host"
                      value={formData.proxy_host}
                      onChange={handleInputChange}
                      placeholder="proxy.example.com"
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
              </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '5px', 
                      fontWeight: 'bold',
                      fontSize: '14px',
                      color: '#333'
                    }}>
                      Proxy Port
                    </label>
                    <input
                      type="number"
                      name="proxy_port"
                      value={formData.proxy_port}
                      onChange={handleInputChange}
                      placeholder="8080"
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
              </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '5px', 
                      fontWeight: 'bold',
                      fontSize: '14px',
                      color: '#333'
                    }}>
                      Proxy Kullanıcı
                    </label>
                    <input
                      type="text"
                      name="proxy_user"
                      value={formData.proxy_user}
                      onChange={handleInputChange}
                      placeholder="username"
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
              </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '5px', 
                      fontWeight: 'bold',
                      fontSize: '14px',
                      color: '#333'
                    }}>
                      Proxy Şifre
                    </label>
                    <input
                      type="text"
                      name="proxy_pass"
                      value={formData.proxy_pass}
                      onChange={handleInputChange}
                      placeholder="password"
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
              </div>
              </div>

                <div style={{ marginBottom: '25px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    color: '#333'
                  }}>
                    <input
                      type="checkbox"
                      name="status"
                      checked={formData.status === 'active'}
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.checked ? 'active' : 'inactive' }))}
                      style={{
                        width: '16px',
                        height: '16px'
                      }}
                    />
                    Firma Aktif
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    color: '#333'
                  }}>
                    <input
                      type="checkbox"
                      name="api_enabled"
                      checked={!!formData.api_enabled}
                      onChange={handleInputChange}
                      disabled={!editingFirm && integrationType === 'vaktinibildir'}
                      style={{
                        width: '16px',
                        height: '16px'
                      }}
                    />
                    OpenCart API Erişimi Açık
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    color: '#333'
                  }}>
                    <input
                      type="checkbox"
                      name="bulk_message_enabled"
                      checked={!!formData.bulk_message_enabled}
                      onChange={handleInputChange}
                      style={{
                        width: '16px',
                        height: '16px'
                      }}
                    />
                    Toplu Mesaj Menüsü Aktif
                  </label>
                </div>

                <div style={{ 
                  display: 'flex', 
                  gap: '10px',
                  flexDirection: isMobile ? 'column' : 'row'
                }}>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    style={{
                      flex: 1,
                      background: '#6c757d',
                      color: 'white',
                      border: 'none',
                      padding: '12px 20px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      flex: 1,
                      background: editingFirm ? '#007bff' : '#28a745',
                      color: 'white',
                      border: 'none',
                      padding: '12px 20px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.7 : 1
                    }}
                  >
                    {loading ? '⏳ İşleniyor...' : (editingFirm ? '💾 Güncelle' : '➕ Ekle')}
                  </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </AdminLayout>
  );
};

export default FirmsPage;